import {
  type Daily,
  type DailyQuestion,
  DailySchema,
  type LocalDate,
  localDateOf,
  type Mode,
  type QuestionGorev,
  type RecapQuestion,
  type RecapQuestionId,
} from '@disdis/shared'
import { create } from 'zustand'
import { log, reason } from '../app/log.js'
import { recap } from './content.js'

/**
 * Günün İçeriği (W3-OVERHAUL §2.6). The app fetches `/daily/<localDate>.json` once per local
 * date, validates it with `DailySchema`, and keeps it for the day. When the file is missing
 * or invalid (offline, the loop skipped a day) the seeded görev generator and the static
 * `content/recap.json` pool carry the day unchanged: `fallback` is a normal state, not an
 * error surface, and no event is added.
 */

const logger = log('daily')

export type DailyStatus = 'idle' | 'loading' | 'ready' | 'fallback'

type DailyStore = {
  date: LocalDate | null
  status: DailyStatus
  daily: Daily | null
  /** Fetches the day's file once; a second call the same day returns the cached answer. */
  load: (date?: LocalDate) => Promise<Daily | null>
}

let inFlight: Promise<Daily | null> | null = null

export const useDailyStore = create<DailyStore>((set, get) => ({
  date: null,
  status: 'idle',
  daily: null,
  load: async (date = localDateOf()) => {
    const current = get()
    if (current.date === date && current.status !== 'idle' && current.status !== 'loading') {
      return current.daily
    }
    if (inFlight && current.date === date) return inFlight
    set({ date, status: 'loading', daily: null })
    inFlight = fetchDaily(date).then((daily) => {
      // A day may have rolled over while the request was out; only the asked date lands.
      if (get().date === date) set({ status: daily ? 'ready' : 'fallback', daily })
      inFlight = null
      return daily
    })
    return inFlight
  },
}))

async function fetchDaily(date: LocalDate): Promise<Daily | null> {
  if (typeof fetch !== 'function') return null
  try {
    const response = await fetch(`/daily/${date}.json`, { cache: 'no-store' })
    if (!response.ok) {
      logger.debug(`no daily file for ${date} (${response.status})`)
      return null
    }
    const parsed = DailySchema.safeParse(await response.json())
    if (!parsed.success) {
      logger.warn(`daily/${date}.json failed DailySchema`, parsed.error.issues[0]?.message)
      return null
    }
    if (parsed.data.date !== date) {
      logger.warn(`daily/${date}.json carries another date`, parsed.data.date)
      return null
    }
    return parsed.data
  } catch (error) {
    // Offline is the common case here; the static pools carry the day.
    logger.debug(`daily/${date}.json not fetched`, reason(error))
    return null
  }
}

/** The day's content, loaded once. Components read it; `AppShell` triggers the load. */
export function useDaily(): { status: DailyStatus; daily: Daily | null } {
  const status = useDailyStore((store) => store.status)
  const daily = useDailyStore((store) => store.daily)
  return { status, daily }
}

/** Doktor Kunduz's line of the day for the Bugün card, or null when the day has none. */
export function dailyTip(daily: Daily | null, mode: Mode): string | null {
  return daily?.tip[mode] ?? null
}

/**
 * One shape for the görev sheet whatever the source: the generated daily question of the
 * lesson the görev asks about, else the static recap question. Daily questions carry text
 * options only and no rendered voice; the recap ones keep their pictures and lines.
 */
export type GorevQuestion = {
  id: RecapQuestionId
  source: 'daily' | 'recap'
  lessonId: RecapQuestion['lessonId']
  prompt: { mini: string; core: string }
  options: { id: string; text: string; visual?: string; correct: boolean }[]
  miniOptions: string[]
  hint: string
  voice: { prompt: string; hint: string } | null
}

export function gorevQuestion(item: QuestionGorev, daily: Daily | null): GorevQuestion {
  const lessonId = item.questionId.slice(2) as RecapQuestion['lessonId']
  const fresh: DailyQuestion | undefined = daily?.questions.find(
    (question) => question.lessonId === lessonId,
  )
  if (fresh) {
    return {
      id: fresh.id as RecapQuestionId,
      source: 'daily',
      lessonId,
      prompt: fresh.prompt,
      options: fresh.options,
      miniOptions: fresh.miniOptions,
      hint: fresh.hint,
      voice: null,
    }
  }
  const stock = recap.questions.find((question) => question.id === item.questionId)
  if (!stock) throw new Error(`content/recap.json has no question ${item.questionId}`)
  return {
    id: stock.id,
    source: 'recap',
    lessonId: stock.lessonId,
    prompt: stock.prompt,
    options: stock.options,
    miniOptions: stock.miniOptions,
    hint: stock.hint,
    voice: stock.voice,
  }
}

/** A playful rename of the day's game target, never a number (W3-OVERHAUL §2.6). */
export function gorevTwist(daily: Daily | null, mode: Mode): string | null {
  return daily?.gorevTwist?.text[mode] ?? null
}
